import { cn } from '../ui/cn';
import { Section } from '../ui';
import { useSeen, useTabSwap } from '../ui/useTabSwap';
import type { ShowcaseEntry } from './showcase';

/**
 * Mise en page compacte: titre de section avec motif de marque, rangée d'onglets en haut,
 * photo encadrée à gauche (liseré orange) et texte à droite.
 * Au changement d'onglet: glissement des photos avec profondeur, texte qui sort puis entre, trait d'onglet.
 */
export default function CardShowcase({ eyebrow, heading, entries }: { eyebrow: string; heading: string; entries: ShowcaseEntry[] }) {
  const { currentId, prevId, dir, revealed, swapping, select } = useTabSwap(entries.map((e) => e.id));
  const [setRef, seen] = useSeen<HTMLDivElement>();

  if (entries.length === 0) return null;
  const current = entries.find((e) => e.id === currentId) ?? entries[0];
  const previous = entries.find((e) => e.id === prevId) ?? null;
  const playReveal = seen && !revealed;

  return (
    <Section eyebrow={eyebrow} heading={heading} pattern="top-right" background="white">
      <div ref={setRef}>
        {/* Une seule rangée d'onglets en haut; défilement horizontal si l'écran est étroit */}
        <div className="-mx-6 mb-10 overflow-x-auto px-6 md:mx-0 md:mb-12 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div role="tablist" className="flex min-w-max gap-x-10 md:gap-x-12">
            {entries.map((e, i) => {
              const active = e.id === current.id;
              const leaving = e.id === prevId;
              return (
                <button
                  key={e.id}
                  role="tab"
                  aria-selected={active}
                  data-visible={seen}
                  onClick={() => select(e.id)}
                  style={{ transitionDelay: seen ? `${i * 90}ms` : undefined }}
                  className={cn('ps-tab-item relative shrink-0 whitespace-nowrap pb-2.5 text-left font-display text-lg font-semibold transition-colors', active ? 'text-charcoal' : 'text-muted hover:text-charcoal')}
                >
                  {e.label}
                  {/* Trait: attend 0,25 s puis grandit sur le nouvel onglet; se retire en 0,1 s sur l'ancien */}
                  <span
                    key={active ? `in-${current.id}-${swapping}` : leaving ? 'out' : 'idle'}
                    aria-hidden
                    className={cn('absolute bottom-0 left-0 h-0.5 bg-brand', active && (swapping ? 'ps-bar-in' : 'w-full'), leaving && 'ps-bar-out')}
                    style={active ? undefined : { width: leaving ? undefined : 0 }}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div className={cn('ps-stage relative aspect-[4/3] overflow-hidden bg-secondary-200', playReveal && 'ps-reveal-img')} style={seen || revealed ? undefined : { clipPath: 'inset(0 100% 0 0)' }}>
            {previous && (
              <div key={`out-${previous.id}`} className={cn('ps-img', dir === 'next' ? 'ps-img-out-next' : 'ps-img-out-prev')}>
                <img src={previous.image} alt="" className="h-full w-full object-cover" />
              </div>
            )}
            <div key={`in-${current.id}`} className={cn('ps-img', swapping && (dir === 'next' ? 'ps-img-in-next' : 'ps-img-in-prev'))}>
              <img src={current.image} alt={current.alt} loading="lazy" className="h-full w-full object-cover" />
            </div>
            <span aria-hidden className="absolute bottom-0 left-0 z-10 h-1.5 w-24 bg-brand" />
          </div>

          <div className="relative grid [&>*]:col-start-1 [&>*]:row-start-1">
            {previous && <div key={`t-out-${previous.id}`} className="ps-text-out pointer-events-none">{previous.content}</div>}
            <div
              key={`t-in-${current.id}`}
              className={cn(swapping ? (dir === 'next' ? 'ps-text-in-next' : 'ps-text-in-prev') : playReveal ? 'ps-reveal-text' : !seen && !revealed && 'opacity-0')}
            >
              {current.content}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
